import { ArrowRight, Plus, Minus, Pencil } from "lucide-react";
import { useT } from "../../lib/i18n";
import { money } from "../../lib/format";

const fmt = (v) => (typeof v === "number" ? money(v) : typeof v === "boolean" ? (v ? "✓" : "✗") : v == null || v === "" ? "—" : String(v));

const Change = ({ c }) => {
  const { t } = useT();
  return (
    <div className="flex flex-wrap items-center gap-2 text-xs">
      <span className="w-28 shrink-0 font-semibold text-neutral-600">{t(`f.${c.field}`)}</span>
      <span className="rounded bg-red-50 px-1.5 py-0.5 text-red-700 line-through">{fmt(c.from)}</span>
      <ArrowRight className="h-3 w-3 text-neutral-400" />
      <span className="rounded bg-emerald-50 px-1.5 py-0.5 font-semibold text-emerald-700">{fmt(c.to)}</span>
    </div>
  );
};

const Group = ({ title, data, render, testid }) => {
  const { t } = useT();
  const none = !data.added.length && !data.removed.length && !data.modified.length;
  return (
    <div data-testid={testid}>
      <h4 className="mb-2 text-xs font-bold uppercase tracking-wider text-neutral-800">{title}</h4>
      {none ? <div className="text-xs text-neutral-400">{t("diff.nochanges")}</div> : (
        <div className="space-y-2">
          {data.added.map((x, i) => <div key={`a${i}`} className="flex items-center gap-2 rounded border-l-4 border-emerald-500 bg-emerald-50 px-3 py-2 text-xs"><Plus className="h-3 w-3 text-emerald-700" /><b className="text-emerald-800">{t("diff.added")}</b>{render(x)}</div>)}
          {data.removed.map((x, i) => <div key={`r${i}`} className="flex items-center gap-2 rounded border-l-4 border-red-500 bg-red-50 px-3 py-2 text-xs"><Minus className="h-3 w-3 text-red-700" /><b className="text-red-800">{t("diff.removed")}</b><span className="line-through">{render(x)}</span></div>)}
          {data.modified.map((x, i) => (
            <div key={`m${i}`} className="space-y-1.5 rounded border-l-4 border-amber-400 bg-amber-50/60 px-3 py-2">
              <div className="flex items-center gap-2 text-xs"><Pencil className="h-3 w-3 text-amber-700" /><b className="text-amber-800">{t("diff.modified")}</b><span className="font-medium">{x.label}</span></div>
              {x.changes.map((c, j) => <Change key={j} c={c} />)}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default function DiffView({ diff, currency }) {
  const { t } = useT();
  return (
    <div className="space-y-6" data-testid="diff-view">
      <div>
        <h4 className="mb-2 text-xs font-bold uppercase tracking-wider">{t("diff.totals")}</h4>
        <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
          {diff.totals.map((x) => {
            const up = x.delta > 0, pctField = x.field === "margin_pct";
            return (
              <div key={x.field} className="rounded border border-neutral-200 bg-white p-3" data-testid={`diff-total-${x.field}`}>
                <div className="text-[10px] font-semibold uppercase tracking-wider text-neutral-500">{t(`f.${x.field}`)}</div>
                <div className="mt-1 font-mono text-sm font-bold">{pctField ? `${x.to}%` : money(x.to, x.field === "total_usd" ? "USD" : currency)}</div>
                <div className={`font-mono text-[11px] ${x.delta === 0 ? "text-neutral-400" : up ? "text-emerald-700" : "text-red-700"}`}>
                  {x.delta === 0 ? "=" : `${up ? "▲ +" : "▼ "}${pctField ? `${x.delta} pp` : money(x.delta)}`}
                </div>
              </div>
            );
          })}
        </div>
      </div>
      <div data-testid="diff-header">
        <h4 className="mb-2 text-xs font-bold uppercase tracking-wider">{t("diff.header")}</h4>
        {diff.header.length ? <div className="space-y-1.5">{diff.header.map((c) => <Change key={c.field} c={c} />)}</div> : <div className="text-xs text-neutral-400">{t("diff.nochanges")}</div>}
      </div>
      <Group testid="diff-lines" title={t("diff.lines")} data={diff.lines} render={(x) => <span>{x.description} · {money(x.net)}</span>} />
      <Group testid="diff-taxes" title={t("diff.taxes")} data={diff.taxes} render={(x) => <span>{x.name} {x.rate}%</span>} />
      <Group testid="diff-techs" title={t("diff.techs")} data={diff.technicians} render={(x) => <span>{x.name} · {x.days} d</span>} />
      <Group testid="diff-machines" title={t("diff.machines")} data={diff.machines} render={(x) => <span>{x.model} #{x.serial_number}</span>} />
    </div>
  );
}
